export const DEVICE_ID_STORAGE_KEY = 'kma_device_id';
export const TAB_ID_SESSION_KEY = 'kma_tab_id';

let cachedDeviceId: string | undefined;
let cachedTabId: string | undefined;

let fallbackUuidCounter = 0;

function normalizeSegment(segment: string, minLen: number = 6): string {
  return segment.length >= minLen ? segment : segment.padEnd(minLen, '0');
}

function fallbackUuid(): string {
  const timeSeg = normalizeSegment(Date.now().toString(36));
  const perfSeg =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? normalizeSegment(Math.floor(performance.now() * 1000).toString(36))
      : normalizeSegment('0');
  const counterSeg = normalizeSegment((fallbackUuidCounter++).toString(36));
  const randSeg1 = normalizeSegment(Math.random().toString(36).slice(2) || '0');
  const randSeg2 = normalizeSegment(Math.random().toString(36).slice(2) || '0');

  // 3+ segments, base36-ish; designed to be stable enough for anonymous client IDs.
  return `${timeSeg}-${perfSeg}-${counterSeg}-${randSeg1}-${randSeg2}`;
}

const UUID_V4ISH_RE = /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i;
// Our fallbackUuid() format: 3+ dash-separated base36-ish segments.
const FALLBACK_ID_RE = /^[\da-z]{6,}(?:-[\da-z]{6,}){2,}$/i;

function isValidStoredId(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  return UUID_V4ISH_RE.test(value) || FALLBACK_ID_RE.test(value);
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return fallbackUuid();
}

function safeGetItem(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(storage: Storage, key: string, value: string): boolean {
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function getOrCreateDeviceId(storage: Storage = localStorage): string {
  if (isValidStoredId(cachedDeviceId)) return cachedDeviceId;

  const existing = safeGetItem(storage, DEVICE_ID_STORAGE_KEY);
  if (isValidStoredId(existing)) {
    cachedDeviceId = existing;
    return existing;
  }

  const id = generateId();
  const didPersist = safeSetItem(storage, DEVICE_ID_STORAGE_KEY, id);

  // Read back to converge quickly if another tab wrote concurrently.
  // Note: there is a theoretical race window where another tab writes between our
  // setItem() and this read; in that case we'll adopt the settled value.
  // This is intentional: the goal is for tabs to converge on a single stable device id.
  if (didPersist) {
    const settled = safeGetItem(storage, DEVICE_ID_STORAGE_KEY);
    cachedDeviceId = isValidStoredId(settled) ? settled : id;
  } else {
    // Storage unavailable (e.g., disabled session/local storage). Keep an in-memory id.
    cachedDeviceId = id;
  }
  return cachedDeviceId;
}

export function getOrCreateTabId(storage: Storage = sessionStorage): string {
  if (isValidStoredId(cachedTabId)) return cachedTabId;

  const existing = safeGetItem(storage, TAB_ID_SESSION_KEY);
  if (isValidStoredId(existing)) {
    cachedTabId = existing;
    return existing;
  }

  const id = generateId();
  const didPersist = safeSetItem(storage, TAB_ID_SESSION_KEY, id);

  if (didPersist) {
    // Read back to converge quickly if storage is updated concurrently.
    // As with the device id, we may observe a different value than we wrote if another
    // context updates storage between calls; adopting the settled value avoids flapping.
    const settled = safeGetItem(storage, TAB_ID_SESSION_KEY);
    cachedTabId = isValidStoredId(settled) ? settled : id;
  } else {
    cachedTabId = id;
  }
  return cachedTabId;
}

// Test helper: allows unit tests to reset module-level caches between cases.
export function __resetSessionIdentityCachesForTests(): void {
  cachedDeviceId = undefined;
  cachedTabId = undefined;
  fallbackUuidCounter = 0;
}

// Test helper: allows unit tests to exercise generateId() without touching storage/caches.
export function __generateIdForTests(): string {
  return generateId();
}

/**
 * Lease token used for the server-side session lock.
 * Includes a stable device id (localStorage) + stable tab id (sessionStorage).
 */
export function getSessionLeaseId(): string {
  const deviceId = getOrCreateDeviceId();
  const tabId = getOrCreateTabId();
  return `${deviceId}:${tabId}`;
}

function detectBrowser(ua: string): string {
  // Check Edge BEFORE Chrome because Edge's UA contains "Chrome/".
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua) && !/Chromium\//.test(ua)) return 'Safari';
  if (/Firefox\//.test(ua)) return 'Firefox';
  return 'Browser';
}

function detectOs(ua: string): string {
  if (/iPad|iPhone|iPod/.test(ua)) return 'iPhone/iPad';
  if (/Android/.test(ua)) return 'Android device';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Mac OS X|Macintosh/.test(ua)) return 'Mac';
  if (/Linux/.test(ua)) return 'Linux PC';
  return 'Device';
}

export function getFriendlyDeviceInfo(userAgent: string = navigator.userAgent): string {
  const os = detectOs(userAgent);
  const browser = detectBrowser(userAgent);
  return `${os} (${browser})`;
}
